// Local-mode fixtures for Settings → Single sign-on (/pam/settings/sso).
// Development only — wired in by mockData.js when REACT_APP_LOCAL_MODE is "true".
//
// Each entry is { pattern, respond(body) }: `pattern` is a lowercase substring
// of the request URL, `respond` gets the parsed JSON body and returns the
// response data. Stateful, so a save or delete shows on the next list. The
// local-mode adapter always answers 200, so the 400 {error} refusals of
// /sso/saveProvider are not reachable here.

const MASKED = "********";
const iso = (msAgo) => new Date(Date.now() - msAgo).toISOString();

const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

let providers = [
  {
    id: "5b0d6c1e-8a42-4f1b-9c3e-2d7f9a1e6b40",
    tenantId: 1,
    kind: "github",
    name: "GitHub",
    clientId: "Iv1.8a61f9b3a7aba766",
    scopes: ["read:user", "user:email", "read:org"],
    emailClaim: "",
    requireEmailVerified: true,
    allowedDomains: ["acme.com"],
    githubOrg: "acme-corp",
    enabled: true,
    hasSecret: true,
    clientSecret: MASKED,
    createdAt: iso(9 * 86400e3),
    updatedAt: iso(2 * 86400e3),
  },
];

// The same normalisation the server applies (sso.normalise), loosely.
function saveProvider(body = {}) {
  const kind = body.kind === "oidc" ? "oidc" : "github";
  const prev = body.id ? providers.find((p) => p.id === body.id) : null;
  const secret = String(body.clientSecret || "").trim();
  const typed = secret !== "" && secret !== MASKED;
  let scopes = (body.scopes || []).filter(Boolean);
  if (!scopes.length) scopes = kind === "github" ? ["read:user", "user:email"] : ["openid", "email", "profile"];
  if (kind === "oidc" && !scopes.includes("openid")) scopes = ["openid", ...scopes];
  if (kind === "github" && body.githubOrg && !scopes.includes("read:org")) scopes = [...scopes, "read:org"];
  const p = {
    id: prev ? prev.id : uuid(),
    tenantId: body.tenantId || 1,
    kind,
    name: String(body.name || "").trim(),
    ...(kind === "oidc" ? { issuer: String(body.issuer || "").trim().replace(/\/+$/, "") } : {}),
    clientId: String(body.clientId || "").trim(),
    scopes,
    emailClaim: kind === "oidc" ? body.emailClaim || "email" : "",
    requireEmailVerified: body.requireEmailVerified !== false,
    allowedDomains: (body.allowedDomains || []).map((d) => String(d).trim().replace(/^@/, "").toLowerCase()).filter(Boolean),
    ...(kind === "github" && body.githubOrg ? { githubOrg: String(body.githubOrg).trim() } : {}),
    enabled: !body.disabled,
    hasSecret: typed || !!prev?.hasSecret,
    createdAt: prev ? prev.createdAt : iso(0),
    updatedAt: iso(0),
  };
  if (p.hasSecret) p.clientSecret = MASKED;
  providers = prev ? providers.map((x) => (x.id === prev.id ? p : x)) : [...providers, p];
  return { provider: p };
}

function deleteProvider(body = {}) {
  providers = providers.filter((p) => p.id !== body.id);
  return { status: "deleted" };
}

export const SSO_MOCKS = [
  { pattern: "/sso/listproviders", respond: () => ({ providers }) },
  { pattern: "/sso/saveprovider", respond: (body) => saveProvider(body || {}) },
  { pattern: "/sso/deleteprovider", respond: (body) => deleteProvider(body || {}) },
];
